// Keys that live on `$route.query` rather than the Vuex filters store (see
// `filtersModel` in GroupIndex.vue). Rescued verbatim so `convertToQueryString`
// still receives filter values raw — see the note below on why they aren't
// merged in via `routeQuery` wholesale.
const ROUTE_ONLY_KEYS = ['search', 'sort', 'creator_type', 'invitations', 'added', 'exclude_enrolled']

/**
 * Single source of truth for combining active filters with the route-query-only
 * params (`search`, `sort`, `creator_type`, `invitations`, `added`, `exclude_enrolled`)
 * into one request params object. Those keys live on the route query, not in the Vuex
 * filters store, so any consumer that reads `filters` alone silently drops them.
 * Used by both the real list-fetch request and the "Show X Results" count
 * preview so they can never drift apart again.
 *
 * Filters are passed through raw (not via `serializeFiltersForQuery`) because
 * `convertToQueryString` already does its own bracket-notation serialization for
 * PHP's `parse_str` — pre-serializing here as well double-encodes any filter
 * whose value is an array of primitives (e.g. `creatorAge`) and silently breaks
 * it server-side. For the same reason, `routeQuery` is not spread wholesale:
 * once synced back from the URL it can carry `serializeFiltersForQuery`'s
 * bracket-suffixed keys (e.g. `creatorAge[]`), which would reintroduce the same
 * double-encoding bug via a different path.
 *
 * @param {Object} filters - Vuex `filters/getFilters` state.
 * @param {Object} routeQuery - `$route.query`.
 * @param {Object} extra - Extra params to merge in last (e.g. `page`).
 * @returns {Object}
 */
const buildFilterSearchParams = (filters, routeQuery = {}, extra = {}) => {
  const hasFilters = Object.keys(filters || {}).length > 0

  const routeOnlyParams = ROUTE_ONLY_KEYS.reduce((acc, key) => {
    if (routeQuery?.[key] !== undefined) {
      acc[key] = routeQuery[key]
    }
    return acc
  }, {})

  const base = hasFilters ? { ...filters, ...routeOnlyParams } : { ...routeQuery }

  return { ...base, ...extra }
}

export default buildFilterSearchParams
